import React, { useMemo, PropsWithChildren } from 'react';
import { withApollo, WithApolloClient } from '@apollo/client/react/hoc';
import Checkbox from '@mui/material/Checkbox';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import { debounce } from 'lodash-es';
import * as Yup from 'yup';
import { gql, TSFixMe } from '@yi/core';
import { i18n_75_characters_maximum } from '@yi/i18n/phrases/i18n_75_characters_maximum';
import { i18n_email } from '@yi/i18n/phrases/i18n_email';
import { i18n_enter_coupon_code } from '@yi/i18n/phrases/i18n_enter_coupon_code';
import { i18n_enter_your_email } from '@yi/i18n/phrases/i18n_enter_your_email';
import { i18n_invalid_email_address } from '@yi/i18n/phrases/i18n_invalid_email_address';
import { i18n_phone } from '@yi/i18n/phrases/i18n_phone';
import { i18n_required } from '@yi/i18n/phrases/i18n_required';

import { navigate, analytics, useStorage } from '~/common';

import { Input, InputWrapper, Wrapper } from './elements';
import { AccountExistsError, ACCOUNT_EXISTS_ERROR } from '../AccountExistsError';
import { Button } from '../buttons';
import { FormErrorWrapper } from '../FormErrorWrapper';
import { useLocale } from '../LocaleProvider';

interface Props {
  buttonDesc: string | React.ReactNode;
  formStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  showPhone?: boolean;
  showOptIn?: boolean;
  email?: string;
  form_id?: string;
  form_name?: string;
  return_url: string;
  registration_type?: string;
  language?: gql.typesClient.Language;
  horizontal?: boolean;
  filledDark?: boolean;
  showFakeCouponForm?: boolean;
  onSuccess?(): void;
}

interface Values {
  email?: string;
  phone?: string;
  confirm_opt_in?: boolean;
}

const initialValues = {
  email: '',
  phone: '',
  confirm_opt_in: false,
};

const SetEmailForm_: React.FC<PropsWithChildren<WithApolloClient<Props>>> = ({
  client,
  ...props
}) => {
  const storage = useStorage();
  const { locale } = useLocale();
  const debouncedEmailValidation = useMemo(
    () =>
      debounce(
        async (email?: string) => {
          if (!email) return true;
          if (typeof client === 'undefined') {
            /*
               if the client is not setup properly, we permit false positive state.
               it's safe because we still will perform the email validation during submitting a form.
             */
            return true;
          }
          const response = await client.mutate({
            mutation: gql.Mutate_ValidateEmail,
            variables: { email },
          });
          const {
            data: {
              validateEmail: { error },
            },
          } = response;
          const isValid = !error;
          return isValid;
        },
        1000,
        { leading: true },
      ),
    [client],
  );

  const validationSchema = ({
    showPhone,
    showOptIn,
  }: {
    showPhone?: boolean;
    showOptIn?: boolean;
  }): Yup.SchemaOf<Values> => {
    const schemaDefinition: Yup.SchemaOf<Values> = Yup.object({
      phone: Yup.string().required(i18n_required(locale)),
      email: Yup.string()
        .email(i18n_invalid_email_address(locale))
        .max(75, i18n_75_characters_maximum(locale))
        .required(i18n_required(locale))
        .test(
          'validateEmail',
          () => <AccountExistsError errorMessage={ACCOUNT_EXISTS_ERROR} />,
          debouncedEmailValidation,
        ),
      confirm_opt_in: Yup.boolean().oneOf([true], i18n_required(locale)),
    });

    const fieldsToValidate = [];
    if (showPhone) {
      fieldsToValidate.push('phone');
    } else {
      fieldsToValidate.push('email');
    }

    if (showOptIn) {
      fieldsToValidate.push('confirm_opt_in');
    }

    return schemaDefinition.pick(fieldsToValidate);
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(props)}
      onSubmit={async (values: Values) => {
        const email = (props.email || values.email) as string;

        if (props.onSuccess) props.onSuccess();
        analytics.sendEmailCaptureEvent(email);
        storage.setEmail(email);
        navigate(props.return_url);
      }}
    >
      {({ isSubmitting, values, handleSubmit, handleReset, status, touched, errors }) => (
        // Using native <form> instead of Formik <Form>
        // because the latter caused Typescript compiler errors
        // as of Formik version 1.5.1
        <Wrapper>
          <form
            style={
              props.horizontal
                ? { display: 'flex', flexDirection: 'row', ...props.formStyle }
                : { ...props.formStyle }
            }
            onReset={handleReset}
            onSubmit={handleSubmit}
          >
            {props.showOptIn && (
              <InputWrapper horizontal={props.horizontal}>
                <Field name="confirm_opt_in">
                  <Checkbox
                    name="confirm_opt_in"
                    checked={values.confirm_opt_in}
                    disabled={isSubmitting}
                  />
                  <div>I confirm that I would like to receive email from Yoga International.</div>
                  <ErrorMessage name="confirm_opt_in" component="div" />
                </Field>
              </InputWrapper>
            )}
            {props.showPhone && (
              <InputWrapper horizontal={props.horizontal}>
                <Field name="phone">
                  {({ field }: { field: FieldProps<Values['phone'], Values> }) => (
                    <>
                      <Input
                        {...field}
                        type="tel"
                        name="phone"
                        inputProps={{ 'aria-label': 'Phone' }}
                        label={i18n_phone(locale)}
                        disabled={isSubmitting}
                        variant="filled"
                        placeholder={i18n_phone(locale)}
                        filledDark={props.filledDark}
                      />
                      <ErrorMessage name="phone" component="div" />
                    </>
                  )}
                </Field>
              </InputWrapper>
            )}
            {!props.showPhone && (
              <>
                <InputWrapper horizontal={props.horizontal}>
                  <Field name="email">
                    {({ field }: { field: FieldProps<Values['email'], Values> }) => (
                      <Input
                        {...field}
                        fullWidth
                        required
                        type="email"
                        inputProps={{ 'aria-label': 'Email' }}
                        name="email"
                        label={i18n_enter_your_email(locale)}
                        placeholder={i18n_email(locale)}
                        disabled={isSubmitting}
                        variant="filled"
                        filledDark={props.filledDark}
                        error={touched.email && Boolean(errors.email)}
                      />
                    )}
                  </Field>
                </InputWrapper>
                {/* Cosmetic only. Does not add a coupon to the cart. */}
                {props.showFakeCouponForm && (
                  <Input
                    inputProps={{ 'aria-label': 'Enter coupon code' }}
                    variant="filled"
                    label={i18n_enter_coupon_code(locale)}
                  />
                )}
              </>
            )}
            {errors?.email && (
              <FormErrorWrapper
                style={{
                  marginBottom: '10px',
                  backgroundColor: '#fff',
                  padding: '5px',
                  fontSize: props.horizontal ? '10px' : undefined,
                }}
              >
                {errors.email}
              </FormErrorWrapper>
            )}
            <Button
              style={
                props.horizontal
                  ? { minWidth: 'auto', height: '100%', ...props.buttonStyle }
                  : { minWidth: '100%', ...props.buttonStyle }
              }
              type="submit"
              disabled={isSubmitting || status?.email}
              showSpinner={isSubmitting}
            >
              {props.buttonDesc}
            </Button>
          </form>
        </Wrapper>
      )}
    </Formik>
  );
};

export const SetEmailForm = withApollo(SetEmailForm_ as TSFixMe) as unknown as React.FC<
  PropsWithChildren<Props>
>;
